import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Calendar, Copy, Heart, Map, Search, Settings, Users } from 'lucide-react';
import SearchGuest from './components/SearchGuest';
import AddGuest from './components/AddGuest';
import GuestList from './components/GuestList';
import FloorPlanEditor from './components/FloorPlanEditor';
import FloorPlanViewer from './components/FloorPlanViewer';
import Landing from './components/Landing';
import EventDashboard from './components/EventDashboard';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import EventShareCard from './components/EventShareCard';
import type { Event, FloorPlan, Guest, User } from './types';
import { getEvent, getFloorPlan, getGuests, logout, saveFloorPlan, verifyToken } from './api';

type AdminView = 'guests' | 'floorplan';

type Route =
  | { name: 'home' }
  | { name: 'login' }
  | { name: 'register' }
  | { name: 'dashboard' }
  | { name: 'superadmin' }
  | { name: 'event-public'; eventId: string }
  | { name: 'event-manage'; eventId: string }
  | { name: 'not-found' };

const normalizePath = (path: string) => {
  if (!path) {
    return '/';
  }

  const normalized = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  return normalized || '/';
};

const parseRoute = (pathname: string): Route => {
  const path = normalizePath(pathname);

  if (path === '/') {
    return { name: 'home' };
  }

  if (path === '/login') {
    return { name: 'login' };
  }

  if (path === '/register') {
    return { name: 'register' };
  }

  if (path === '/dashboard') {
    return { name: 'dashboard' };
  }

  if (path === '/superadmin') {
    return { name: 'superadmin' };
  }

  const manageMatch = path.match(/^\/event\/([^/]+)\/manage$/);
  if (manageMatch) {
    return { name: 'event-manage', eventId: manageMatch[1] };
  }

  const eventMatch = path.match(/^\/event\/([^/]+)$/);
  if (eventMatch) {
    return { name: 'event-public', eventId: eventMatch[1] };
  }

  return { name: 'not-found' };
};

const formatEventDate = (date?: string) => {
  if (!date) {
    return 'Date TBD';
  }

  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default function App() {
  const [pathname, setPathname] = useState(() => normalizePath(window.location.pathname));
  const [user, setUser] = useState<Omit<User, 'passwordHash'> | null>(null);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [eventRouteStatus, setEventRouteStatus] = useState<'idle' | 'loading' | 'ready'>('idle');

  const [adminView, setAdminView] = useState<AdminView>('guests');
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [isLoadingGuests, setIsLoadingGuests] = useState(false);
  const [floorPlan, setFloorPlan] = useState<FloorPlan | null>(null);
  const [isLoadingFloorPlan, setIsLoadingFloorPlan] = useState(false);
  const [selectionPulse, setSelectionPulse] = useState(0);
  const guestResultRef = useRef<HTMLDivElement | null>(null);

  const route = useMemo(() => parseRoute(pathname), [pathname]);
  const routeEventId = route.name === 'event-public' || route.name === 'event-manage' ? route.eventId : null;
  const isEventOwner = Boolean(user && currentEvent && currentEvent.userId === user.id);
  const publicEventUrl = currentEvent ? `${window.location.origin}/event/${currentEvent.id}` : '';

  const navigate = (path: string, options?: { replace?: boolean }) => {
    const nextPath = normalizePath(path);

    if (options?.replace) {
      window.history.replaceState({}, '', nextPath);
    } else {
      window.history.pushState({}, '', nextPath);
    }

    setPathname(nextPath);
  };

  useEffect(() => {
    const handlePopState = () => {
      setPathname(normalizePath(window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        if (!isCancelled) {
          setUser(null);
          setCheckingAuth(false);
        }
        return;
      }

      const response = await verifyToken();

      if (isCancelled) {
        return;
      }

      if (response.success && response.user) {
        setUser(response.user);
      } else {
        logout();
        setUser(null);
      }

      setCheckingAuth(false);
    };

    checkAuth();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (checkingAuth) {
      return;
    }

    if (user && (route.name === 'home' || route.name === 'login' || route.name === 'register')) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!user && route.name === 'dashboard') {
      navigate('/login', { replace: true });
      return;
    }

    if (route.name === 'not-found') {
      navigate(user ? '/dashboard' : '/', { replace: true });
    }
  }, [checkingAuth, route.name, user]);

  useEffect(() => {
    if (route.name !== 'event-public' && route.name !== 'event-manage') {
      setCurrentEvent(null);
      setSelectedGuest(null);
      setGuests([]);
      setFloorPlan(null);
      setEventRouteStatus('idle');
    }
  }, [route.name]);

  useEffect(() => {
    if (!routeEventId || checkingAuth) {
      return;
    }

    let isCancelled = false;

    if (currentEvent?.id === routeEventId) {
      setEventRouteStatus('ready');
      return;
    }

    const loadEvent = async () => {
      setEventRouteStatus('loading');
      setSelectedGuest(null);

      const response = await getEvent(routeEventId);

      if (isCancelled) {
        return;
      }

      if (response.success && response.event) {
        setCurrentEvent(response.event);
        setEventRouteStatus('ready');
      } else {
        setCurrentEvent(null);
        setEventRouteStatus('idle');
        navigate(user ? '/dashboard' : '/', { replace: true });
      }
    };

    loadEvent();

    return () => {
      isCancelled = true;
    };
  }, [checkingAuth, currentEvent?.id, routeEventId, user]);

  useEffect(() => {
    if (
      checkingAuth ||
      route.name !== 'event-manage' ||
      !routeEventId ||
      !currentEvent ||
      currentEvent.id !== routeEventId
    ) {
      return;
    }

    if (!user || currentEvent.userId !== user.id) {
      navigate(`/event/${routeEventId}`, { replace: true });
    }
  }, [checkingAuth, currentEvent, route, routeEventId, user]);

  useEffect(() => {
    if (!currentEvent) {
      return;
    }

    setSelectedGuest(null);
    loadGuests();
    loadFloorPlan();
  }, [currentEvent?.id]);

  useEffect(() => {
    if (route.name !== 'event-public' || !selectedGuest) {
      return;
    }

    setSelectionPulse((value) => value + 1);

    const scrollTimeout = window.setTimeout(() => {
      guestResultRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 140);

    return () => window.clearTimeout(scrollTimeout);
  }, [route.name, selectedGuest?.id]);

  const loadGuests = async () => {
    if (!currentEvent) {
      return;
    }

    setIsLoadingGuests(true);

    try {
      const response = await getGuests(currentEvent.id);
      if (response.success && response.guests) {
        setGuests(response.guests);
      }
    } catch (error) {
      console.error('Failed to load guests:', error);
    } finally {
      setIsLoadingGuests(false);
    }
  };

  const loadFloorPlan = async () => {
    if (!currentEvent) {
      return;
    }

    setIsLoadingFloorPlan(true);

    try {
      const response = await getFloorPlan(currentEvent.id);
      if (response.success && response.floorPlan) {
        setFloorPlan(response.floorPlan);
      } else {
        setFloorPlan(null);
      }
    } catch (error) {
      console.error('Failed to load floor plan:', error);
    } finally {
      setIsLoadingFloorPlan(false);
    }
  };

  const handleGuestAdded = (guest: Guest) => {
    setGuests((prevGuests) => [...prevGuests, guest]);
  };

  const handleGuestUpdated = () => {
    setSelectedGuest(null);
    loadGuests();
  };

  const handleGuestDeleted = (deletedGuestId: string) => {
    setGuests((prevGuests) => prevGuests.filter((guest) => guest.id !== deletedGuestId));
  };

  const handleCopyEventURL = () => {
    if (!currentEvent) {
      return;
    }

    const url = `${window.location.origin}/event/${currentEvent.id}`;
    navigator.clipboard.writeText(url).then(() => {
      alert('Event URL copied to clipboard.');
    }).catch(() => {
      alert(`Failed to copy URL. Please copy it manually: ${url}`);
    });
  };

  const handleFloorPlanUpdate = (updatedFloorPlan: FloorPlan) => {
    setFloorPlan(updatedFloorPlan);
  };

  const handleFloorPlanSave = async (floorPlanToSave: FloorPlan) => {
    try {
      const response = await saveFloorPlan({ floorPlan: floorPlanToSave });
      if (response.success && response.floorPlan) {
        setFloorPlan(response.floorPlan);
        alert('Floor plan saved successfully.');
      } else {
        alert(`Failed to save floor plan: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Failed to save floor plan. Please try again.');
    }
  };

  const handleAuthenticated = (authenticatedUser: Omit<User, 'passwordHash'>) => {
    setUser(authenticatedUser);
    navigate('/dashboard', { replace: true });
  };

  const handleEventSelect = (event: Event) => {
    setCurrentEvent(event);
    setAdminView('guests');
    setSelectedGuest(null);
    navigate(`/event/${event.id}`);
  };

  const handleBackToDashboard = () => {
    setAdminView('guests');
    setSelectedGuest(null);
    navigate('/dashboard');
  };

  const handleAppLogout = () => {
    setUser(null);
    setCurrentEvent(null);
    setSelectedGuest(null);
    setAdminView('guests');
    navigate('/', { replace: true });
  };

  const renderLoadingState = (message: string) => (
    <div className="status-screen">
      <div className="card status-card">
        <div className="spinner" />
        <p>{message}</p>
      </div>
    </div>
  );

  if (checkingAuth) {
    return renderLoadingState('Checking your session...');
  }

  if (route.name === 'superadmin') {
    return <SuperAdminDashboard onNavigateHome={() => navigate('/')} />;
  }

  if (route.name === 'home' || route.name === 'login' || route.name === 'register') {
    const page = route.name === 'home' ? 'landing' : route.name;
    return <Landing page={page} onAuthenticated={handleAuthenticated} onNavigate={navigate} />;
  }

  if (route.name === 'dashboard' && user) {
    return <EventDashboard user={user} onEventSelect={handleEventSelect} onLogout={handleAppLogout} />;
  }

  if ((route.name === 'event-public' || route.name === 'event-manage') && eventRouteStatus === 'loading') {
    return renderLoadingState('Loading event...');
  }

  if ((route.name === 'event-public' || route.name === 'event-manage') && currentEvent) {
    const eventDate = formatEventDate(currentEvent.date);

    if (route.name === 'event-public') {
      return (
        <div className="container">
          <section className="page-hero card">
            <div className="page-hero__content">
              <span className="eyebrow">Public Event Page</span>
              <h1 className="page-title">
                <Heart size={28} />
                {currentEvent.name}
              </h1>
              <p className="page-copy">
                {currentEvent.description || 'Search for a guest name to find the correct table and view the venue layout.'}
              </p>

              <div className="hero-badges">
                <div className="info-chip">
                  <Calendar size={16} />
                  {eventDate}
                </div>
                <div className="info-chip">
                  <Users size={16} />
                  {guests.length} guest{guests.length === 1 ? '' : 's'}
                </div>
              </div>
            </div>

            <div className="hero-actions">
              {isEventOwner && (
                <button className="btn btn-secondary" onClick={handleBackToDashboard}>
                  <ArrowLeft size={16} />
                  Back to Dashboard
                </button>
              )}
              <button className="btn btn-secondary" onClick={handleCopyEventURL}>
                <Copy size={16} />
                Copy Event URL
              </button>
              {isEventOwner && (
                <button className="btn" onClick={() => navigate(`/event/${currentEvent.id}/manage`)}>
                  <Settings size={16} />
                  Manage Event
                </button>
              )}
            </div>
          </section>

          <div className="page-grid page-grid--event">
            <section className="card panel-card">
              <div className="section-heading">
                <h2>Find your table</h2>
                <p>Start typing a guest name to see matching results.</p>
              </div>

              <SearchGuest
                onGuestSelect={setSelectedGuest}
                guests={guests}
                isLoading={isLoadingGuests}
                eventId={currentEvent.id}
              />

              {!selectedGuest && (
                <div className="empty-state-panel">
                  <Search size={20} />
                  <span>Select a guest to reveal the assigned table and venue map.</span>
                </div>
              )}
            </section>

            <section className="content-stack">
              {selectedGuest && (
                <div
                  ref={guestResultRef}
                  key={`guest-result-${selectedGuest.id}-${selectionPulse}`}
                  className="card selected-guest-card selection-spotlight"
                >
                  <div className="selected-guest-copy">
                    <span className="eyebrow">Welcome</span>
                    <div className="selected-guest-name">Welcome {selectedGuest.name}</div>
                    <p className="selected-guest-message">
                      Your are assigned to table number {selectedGuest.table}.
                    </p>
                  </div>
                  <div className="selected-guest-table">Table {selectedGuest.table}</div>
                  <button className="btn btn-secondary" onClick={() => setSelectedGuest(null)}>
                    Search Again
                  </button>
                </div>
              )}

              {selectedGuest && floorPlan && (
                <div
                  key={`guest-floorplan-${selectedGuest.id}-${selectionPulse}`}
                  className="selection-spotlight selection-spotlight--delayed"
                >
                  <FloorPlanViewer
                    floorPlan={floorPlan}
                    highlightedTableNumber={selectedGuest.table}
                    guestName={selectedGuest.name}
                  />
                </div>
              )}

              {selectedGuest && !floorPlan && isLoadingFloorPlan && (
                <div className="card empty-state-panel empty-state-panel--card">
                  <div className="spinner spinner--small" />
                  <span>Loading venue floor plan...</span>
                </div>
              )}

              {selectedGuest && !floorPlan && !isLoadingFloorPlan && (
                <div className="card empty-state-panel empty-state-panel--card">
                  <Map size={20} />
                  <span>Floor plan not available for this event yet.</span>
                  <button className="btn btn-secondary" onClick={loadFloorPlan}>
                    Retry Loading Floor Plan
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      );
    }

    return (
      <div className="container">
        <section className="page-hero card">
          <div className="page-hero__content">
            <span className="eyebrow">Event Management</span>
            <h1 className="page-title">
              <Settings size={28} />
              {currentEvent.name}
            </h1>
            <p className="page-copy">
              Manage the guest list, update floor plans, and keep the public event page ready to share.
            </p>

            <div className="hero-badges">
              <div className="info-chip">
                <Calendar size={16} />
                {eventDate}
              </div>
              <div className="info-chip">
                <Users size={16} />
                {guests.length} guest{guests.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div className="hero-actions">
            <button className="btn btn-secondary" onClick={handleBackToDashboard}>
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
            <button className="btn btn-secondary" onClick={() => navigate(`/event/${currentEvent.id}`)}>
              <Search size={16} />
              View Public Page
            </button>
            <button className="btn" onClick={handleCopyEventURL}>
              <Copy size={16} />
              Copy Event URL
            </button>
          </div>
        </section>

        <div className="welcome-banner">
          <div>
            <h3>Event capacity overview</h3>
            <p>You currently have {guests.length} of {user?.guestLimit || 20} guest slots in use.</p>
          </div>
        </div>

        <EventShareCard eventName={currentEvent.name} publicUrl={publicEventUrl} />

        <div className="card panel-card">
          <div className="segmented-control">
            <button
              className={`btn ${adminView === 'guests' ? '' : 'btn-secondary'}`}
              onClick={() => setAdminView('guests')}
            >
              <Users size={16} />
              Guest Management
            </button>
            <button
              className={`btn ${adminView === 'floorplan' ? '' : 'btn-secondary'}`}
              onClick={() => setAdminView('floorplan')}
            >
              <Map size={16} />
              Floor Plan Editor
            </button>
          </div>
        </div>

        {adminView === 'guests' ? (
          <div className="content-stack">
            <AddGuest
              onGuestAdded={handleGuestAdded}
              eventId={currentEvent.id}
              currentGuestCount={guests.length}
              guestLimit={user?.guestLimit || 20}
            />
            <GuestList
              onGuestUpdated={handleGuestUpdated}
              onGuestDeleted={handleGuestDeleted}
              guests={guests}
              isLoading={isLoadingGuests}
              eventId={currentEvent.id}
            />
          </div>
        ) : (
          <div className="content-stack">
            {floorPlan ? (
              <FloorPlanEditor
                floorPlan={floorPlan}
                onFloorPlanUpdate={handleFloorPlanUpdate}
                onSave={handleFloorPlanSave}
              />
            ) : isLoadingFloorPlan ? (
              <div className="card empty-state-panel empty-state-panel--card">
                <div className="spinner spinner--small" />
                <span>Loading floor plan...</span>
              </div>
            ) : (
              <div className="card empty-state-panel empty-state-panel--card">
                <Map size={20} />
                <span>No floor plan found for this event.</span>
                <button className="btn btn-secondary" onClick={loadFloorPlan}>
                  Retry Loading
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return renderLoadingState('Preparing the page...');
}
